// "Add to cal" on an action: a 30-minute reminder at 10am, in the operator's own time
// zone, on their own Google Calendar, named for the action and linking back to it. A
// split button as "Push by 7 days" is: the due date by default, the deadline or a custom
// day and time from its menu. See docs/Y2026-Q4/design-add-to-calendar.md.
import { useCallback, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { GoogleAuthError } from "../api/google-auth";
import { addReminder, formatTime, REMINDER_TIME } from "../api/googleCalendar";
import { formatDay, isoDay, localDay } from "../utils/attributeFormat";
import { Flyout } from "./ActionControls";
import styles from "./NextActionTable.module.css";
import own from "./ActionControls.module.css";

/** The day a reminder goes on: one already past moves to today, where it can still
 * remind. */
const reminderDay = (day: string, today: string): string => (day < today ? today : day);

/** "10am on 3 Nov 2026". */
const when = (day: string, time: string): string => `${formatTime(time)} on ${formatDay(day)}`;

const TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

// The custom time's choices: every hour, and the quarter hours (Noaa, 28 Oct 2026).
const HOURS = Array.from({ length: 24 }, (_, h) => String(h).padStart(2, "0"));
const MINUTES = ["00", "15", "30", "45"];

export const AddToCalendarControl = ({
  actionId,
  title,
  due,
  deadline,
  today,
  description,
}: {
  actionId: string;
  title: string;
  /** The action's own target date; "" when it has none. */
  due: string;
  /** The open deadline it serves; "" when it serves none. */
  deadline: string;
  today: string;
  description: string;
}) => {
  const [menu, setMenu] = useState(false);
  const [custom, setCustom] = useState(false);
  const [picked, setPicked] = useState("");
  const [time, setTime] = useState(REMINDER_TIME);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [added, setAdded] = useState<{
    day: string;
    time: string;
    link: string;
    existing: boolean;
  } | null>(null);
  const anchor = useRef<HTMLSpanElement>(null);
  const closeMenu = useCallback(() => {
    setMenu(false);
    setCustom(false);
    setPicked("");
    setTime(REMINDER_TIME);
  }, []);

  const add = async (day: string, at: string = REMINDER_TIME) => {
    if (busy) return;
    closeMenu();
    setBusy(true);
    setError("");
    try {
      const saved = await addReminder({ actionId, day, time: at, title, description });
      setAdded({ day, time: at, ...saved });
    } catch (err) {
      setAdded(null);
      setError(
        err instanceof GoogleAuthError && err.isUserCancelled
          ? "Google sign-in was closed or blocked - press again, allowing pop-ups for this site if asked."
          : err instanceof Error
            ? err.message
            : String(err),
      );
    } finally {
      setBusy(false);
    }
  };

  const choices = [
    { label: "On due date", day: due },
    { label: "On deadline", day: deadline },
  ];
  // With no due date the default is the deadline, and with neither the menu opens.
  const fallback = due || deadline;

  return (
    <>
      <span ref={anchor} className={own.split}>
        <button
          type="button"
          className={`${styles.button} ${own.splitMain}`}
          disabled={busy}
          title={
            fallback
              ? `${when(reminderDay(fallback, today), REMINDER_TIME)}, for 30 minutes`
              : "Pick a day"
          }
          onClick={() => (fallback ? void add(reminderDay(fallback, today)) : setMenu(true))}
        >
          {busy ? "Adding…" : "Add to cal"}
        </button>
        <button
          type="button"
          className={`${styles.button} ${own.splitToggle} ${own.chevron} ${menu ? own.chevronOpen : ""}`}
          aria-label="Other days to add it on"
          aria-expanded={menu}
          disabled={busy}
          onClick={() => (menu ? closeMenu() : setMenu(true))}
        />
      </span>
      {menu && (
        <Flyout anchor={anchor} className={own.menu} role="menu" onClose={closeMenu}>
          {choices.map(({ label, day }) => (
            <button
              key={label}
              type="button"
              role="menuitem"
              className={own.menuItem}
              disabled={!day}
              onClick={() => void add(reminderDay(day, today))}
            >
              <span>{label}</span>
              <span className={styles.small}>
                {!day
                  ? "none set"
                  : day < today
                    ? `${formatTime(REMINDER_TIME)} today (${formatDay(day)} has passed)`
                    : when(day, REMINDER_TIME)}
              </span>
            </button>
          ))}
          <button
            type="button"
            role="menuitem"
            className={own.menuItem}
            aria-expanded={custom}
            onClick={() => setCustom((open) => !open)}
          >
            On custom date / time…
          </button>
          {custom && (
            <span className={own.custom}>
              <DatePicker
                inline
                calendarClassName={own.calendar}
                calendarStartDay={1}
                selected={picked ? localDay(picked) : null}
                openToDate={localDay(picked || (due > today ? due : today))}
                minDate={localDay(today)}
                highlightDates={due ? [localDay(due)] : []}
                onChange={(date: Date | null) => setPicked(date ? isoDay(date) : "")}
              />
              {due && <span className={styles.small}>Ringed: due {formatDay(due)}</span>}
              <div className={own.field} role="group" aria-labelledby={`${actionId}-time`}>
                <span id={`${actionId}-time`}>Time ({TIME_ZONE})</span>
                <span className={own.timeRow}>
                  <select
                    aria-label="Hour"
                    value={time.slice(0, 2)}
                    onChange={(e) => setTime(`${e.target.value}:${time.slice(3)}`)}
                  >
                    {HOURS.map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                  :
                  <select
                    aria-label="Minutes"
                    value={time.slice(3)}
                    onChange={(e) => setTime(`${time.slice(0, 2)}:${e.target.value}`)}
                  >
                    {MINUTES.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </span>
              </div>
              <button
                type="button"
                className={`${styles.button} ${styles.primary}`}
                disabled={!picked}
                onClick={() => void add(picked, time)}
              >
                {picked ? `Add at ${when(picked, time)}` : "Pick a date"}
              </button>
            </span>
          )}
        </Flyout>
      )}
      {added && (
        <span className={styles.small} role="status">
          {added.existing ? "Already on your calendar" : "On your calendar"} at{" "}
          {when(added.day, added.time)}.{" "}
          <a href={added.link} target="_blank" rel="noreferrer">
            Open
          </a>
        </span>
      )}
      {error && <p className={own.error}>{error}</p>}
    </>
  );
};
